import React, { useEffect, useState, useCallback } from 'react';
import {
  Cliente,
  Modalidade,
  getClientes,
  createCliente,
  updateCliente,
  deleteCliente,
} from '../services/clienteService';
import { getAccounts } from '../services/apiService';
import { AccountAutocomplete } from '../components/ui/AccountAutocomplete';

const MODALIDADE_OPTIONS: { value: Modalidade; label: string; hint: string }[] = [
  {
    value: 'pre_recebimento',
    label: 'Pré-recebimento',
    hint: 'Recebe antes de emitir NF (Adiantamento → Receita)',
  },
  {
    value: 'pos_recebimento',
    label: 'Pós-recebimento',
    hint: 'Emite NF antes de receber (Contas a Receber → Receita)',
  },
];

const MOEDA_OPTIONS = ['BRL', 'USD', 'EUR', 'GBP'];

const MODALIDADE_SHORT: Record<string, string> = {
  pre_recebimento: 'Pré',
  pos_recebimento: 'Pós',
};

interface FormState {
  nome: string;
  aliasesText: string;
  modalidade: Modalidade;
  cnpj: string;
  conta_adiantamento: string;
  conta_receber: string;
  conta_receita: string;
  pais: string;
  cep: string;
  cidade: string;
  estado: string;
  logradouro: string;
  moeda_servico: string;
}

const emptyForm: FormState = {
  nome: '',
  aliasesText: '',
  modalidade: 'pos_recebimento',
  cnpj: '',
  conta_adiantamento: '',
  conta_receber: '',
  conta_receita: '',
  pais: '',
  cep: '',
  cidade: '',
  estado: '',
  logradouro: '',
  moeda_servico: 'BRL',
};

function parseAliases(text: string): string[] {
  return text
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
}

export default function ClientesPage() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingNome, setEditingNome] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadClientes = useCallback(async () => {
    try {
      setClientes(await getClientes());
    } catch {
      setError('Erro ao carregar clientes');
    }
  }, []);

  useEffect(() => {
    loadClientes();
    getAccounts().then(a => setAccounts(a.accounts)).catch(() => {});
  }, [loadClientes]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingNome(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (c: Cliente) => {
    setEditingNome(c.nome);
    setForm({
      nome: c.nome,
      aliasesText: (c.aliases || []).join('\n'),
      modalidade: (c.modalidade || 'pos_recebimento') as Modalidade,
      cnpj: c.cnpj,
      conta_adiantamento: c.conta_adiantamento,
      conta_receber: c.conta_receber,
      conta_receita: c.conta_receita,
      pais: c.pais,
      cep: c.cep,
      cidade: c.cidade,
      estado: c.estado,
      logradouro: c.logradouro,
      moeda_servico: c.moeda_servico || 'BRL',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const isPreMode = form.modalidade === 'pre_recebimento';
    const payload = {
      aliases: parseAliases(form.aliasesText),
      modalidade: form.modalidade,
      cnpj: form.cnpj,
      // só envia a conta da modalidade atual — evita persistir conta da modalidade oposta
      conta_adiantamento: isPreMode ? form.conta_adiantamento : '',
      conta_receber: isPreMode ? '' : form.conta_receber,
      conta_receita: form.conta_receita,
      pais: form.pais,
      cep: form.cep,
      cidade: form.cidade,
      estado: form.estado,
      logradouro: form.logradouro,
      moeda_servico: form.moeda_servico,
    };
    try {
      if (editingNome) {
        await updateCliente(editingNome, payload);
      } else {
        await createCliente({ nome: form.nome, ...payload });
      }
      resetForm();
      loadClientes();
      setSuccess(editingNome ? 'Cliente atualizado' : 'Cliente cadastrado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (c: Cliente) => {
    if (!window.confirm(`Remover cliente "${c.nome}"?`)) return;
    try {
      await deleteCliente(c.nome);
      loadClientes();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao remover');
    }
  };

  const isPre = form.modalidade === 'pre_recebimento';

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">
              Novo Cliente
            </button>
          )}
        </div>

        {error && <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 rounded">{error}</div>}
        {success && <div className="mb-4 p-3 text-sm text-green-700 bg-green-50 rounded">{success}</div>}

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-white rounded-lg shadow space-y-3">
            <h2 className="font-semibold text-gray-800">{editingNome ? `Editar ${editingNome}` : 'Novo Cliente'}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Nome</label>
                <input
                  placeholder="Nome do cliente (ex.: XDPNet)"
                  value={form.nome}
                  onChange={e => setForm({ ...form, nome: e.target.value })}
                  required
                  disabled={!!editingNome}
                  className="block w-full rounded border px-3 py-2 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Modalidade</label>
                <select
                  value={form.modalidade}
                  onChange={e => setForm({ ...form, modalidade: e.target.value as Modalidade })}
                  className="block w-full rounded border px-3 py-2"
                >
                  {MODALIDADE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {MODALIDADE_OPTIONS.find(o => o.value === form.modalidade)?.hint}
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">CNPJ (opcional p/ exterior)</label>
                <input
                  placeholder="00.000.000/0000-00"
                  value={form.cnpj}
                  onChange={e => setForm({ ...form, cnpj: e.target.value })}
                  className="block w-full rounded border px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Moeda de Serviço</label>
                <select
                  value={form.moeda_servico}
                  onChange={e => setForm({ ...form, moeda_servico: e.target.value })}
                  className="block w-full rounded border px-3 py-2"
                >
                  {MOEDA_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">
                  Aliases (um por linha — regex, p/ matching de NF/extrato)
                </label>
                <textarea
                  placeholder={'XDPNET\nXDPNet,?\\s*Inc\\.?'}
                  value={form.aliasesText}
                  onChange={e => setForm({ ...form, aliasesText: e.target.value })}
                  rows={3}
                  className="block w-full rounded border px-3 py-2 font-mono text-sm"
                />
              </div>

              {isPre ? (
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">Conta Adiantamento</label>
                  <AccountAutocomplete
                    value={form.conta_adiantamento}
                    accounts={accounts}
                    onChange={v => setForm({ ...form, conta_adiantamento: v })}
                    placeholder="Passivo:Adiantamentos de Clientes:..."
                    className="block w-full rounded border px-3 py-2"
                  />
                </div>
              ) : (
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">Conta a Receber</label>
                  <AccountAutocomplete
                    value={form.conta_receber}
                    accounts={accounts}
                    onChange={v => setForm({ ...form, conta_receber: v })}
                    placeholder="Ativos:Contas a Receber:..."
                    className="block w-full rounded border px-3 py-2"
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Conta Receita</label>
                <AccountAutocomplete
                  value={form.conta_receita}
                  accounts={accounts}
                  onChange={v => setForm({ ...form, conta_receita: v })}
                  placeholder="Receitas:Serviços:..."
                  className="block w-full rounded border px-3 py-2"
                />
              </div>

              <details className="md:col-span-2">
                <summary className="text-sm text-gray-600 cursor-pointer select-none">Endereço (opcional)</summary>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <input placeholder="País (ISO, ex: BR / US)" value={form.pais} onChange={e => setForm({ ...form, pais: e.target.value })} className="block w-full rounded border px-3 py-2" />
                  <input placeholder="CEP" value={form.cep} onChange={e => setForm({ ...form, cep: e.target.value })} className="block w-full rounded border px-3 py-2" />
                  <input placeholder="Cidade" value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} className="block w-full rounded border px-3 py-2" />
                  <input placeholder="Estado" value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })} className="block w-full rounded border px-3 py-2" />
                  <input placeholder="Logradouro" value={form.logradouro} onChange={e => setForm({ ...form, logradouro: e.target.value })} className="block w-full rounded border px-3 py-2 md:col-span-2" />
                </div>
              </details>
            </div>

            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">Salvar</button>
              <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Cancelar</button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-sm text-gray-600">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Modalidade</th>
                <th className="px-4 py-3">CNPJ / País</th>
                <th className="px-4 py-3">Moeda</th>
                <th className="px-4 py-3">Aliases</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {clientes.map(c => (
                <tr key={c.nome} className="text-sm hover:bg-blue-50 even:bg-gray-100">
                  <td className="px-4 py-3 font-medium">{c.nome}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${c.modalidade === 'pre_recebimento' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {MODALIDADE_SHORT[c.modalidade] || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.cnpj || c.pais || '—'}</td>
                  <td className="px-4 py-3">{c.moeda_servico}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{(c.aliases || []).join(', ')}</td>
                  <td className="px-4 py-3 space-x-2">
                    <button onClick={() => handleEdit(c)} className="text-blue-600 hover:underline text-sm">Editar</button>
                    <button onClick={() => handleDelete(c)} className="text-red-600 hover:underline text-sm">Remover</button>
                  </td>
                </tr>
              ))}
              {clientes.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Nenhum cliente cadastrado</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
